/**
 * Keeps the muted looping videos (the ones under the reviews) playing, also on iPhone:
 * - starts them again when Safari pauses them (scrolling, tab switch, power saving) or when a loop stalls,
 * - starts them when they scroll into view,
 * - starts them on the first touch of the page when iOS refuses to autoplay (Low Power Mode).
 */

const VIDEOS = '.before-after__video';

/** @param {HTMLVideoElement} video */
function keepPlaying(video) {
  if (video.dataset.keepPlaying === 'true') return;
  video.dataset.keepPlaying = 'true';

  // iOS only autoplays inline, muted video, and the muted state must be set as a property too.
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('disableremoteplayback', '');

  const play = () => {
    video.muted = true;
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  };

  // Paused by the system (not by a visitor: there are no controls): start again.
  video.addEventListener('pause', () => {
    if (!video.ended && document.visibilityState === 'visible') setTimeout(play, 150);
  });

  // Some browsers stall at the end of a loop: jump back to the start.
  video.addEventListener('ended', () => {
    video.currentTime = 0;
    play();
  });

  video.addEventListener('stalled', play);
  video.addEventListener('suspend', () => {
    if (video.paused) play();
  });

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) play();
        });
      },
      { threshold: 0.2 }
    ).observe(video);
  }

  play();
}

function init() {
  document.querySelectorAll(VIDEOS).forEach((video) => {
    if (video instanceof HTMLVideoElement) keepPlaying(video);
  });
}

function playAll() {
  document.querySelectorAll(VIDEOS).forEach((video) => {
    if (video instanceof HTMLVideoElement && video.paused) {
      video.muted = true;
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }
  });
}

// Coming back to the tab / app: start again.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') playAll();
});

// iOS Low Power Mode blocks autoplay until the first touch: use it to start the videos.
['touchstart', 'pointerdown', 'click', 'scroll'].forEach((type) => {
  document.addEventListener(type, playAll, { once: false, passive: true });
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

window.addEventListener('load', init);
// The page content can be swapped without a reload (colour switcher): hook up new videos too.
new MutationObserver(init).observe(document.documentElement, { childList: true, subtree: true });
